// App.jsx — router + top-level layout ONLY.
// Convention: this file must stay under 100 lines. Push logic into routes/*.

import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useAuth } from './lib/auth';

import Login from './routes/auth/Login.jsx';
import Signup from './routes/auth/Signup.jsx';
import ForgotPassword from './routes/auth/ForgotPassword.jsx';
import ResetPassword from './routes/auth/ResetPassword.jsx';
import Dashboard from './routes/dashboard/index.jsx';
import HorsesList from './routes/horses/Index.jsx';
import HorseNew from './routes/horses/new.jsx';
import HorseDetail from './routes/horses/Detail.jsx';
import HorseEdit from './routes/horses/edit.jsx';
import OwnersDirectory from './routes/owners/Index.jsx';
import OwnerDetail from './routes/owners/Detail.jsx';
import RidersDirectory from './routes/riders/Index.jsx';
import RiderDetail from './routes/riders/Detail.jsx';
import ServiceProviders from './routes/providers/Index.jsx';
import TeamPage from './routes/team/Index.jsx';
import LessonTypesPage from './routes/settings/LessonTypes.jsx';
import ShiftTypesPage from './routes/settings/ShiftTypes.jsx';
import RoutinesPage from './routes/settings/Routines.jsx';
import BarnSettingsPage from './routes/settings/BarnSettings.jsx';
import ArchivePage from './routes/settings/Archive.jsx';
import BookPage from './routes/schedule/Book.jsx';
import SupplementsPage from './routes/supplements/Supplements';
import SupplementDetail from './routes/supplements/SupplementDetail';
import SupplementEdit from './routes/supplements/SupplementEdit';
import SupplementHistory from './routes/supplements/SupplementHistory';
import SupplementOrders from './routes/supplements/SupplementOrders';
import AvailabilityPage from './routes/schedule/Availability.jsx';
import ShiftsPage from './routes/schedule/Shifts.jsx';
import DailyBoard from './routes/board/Daily.jsx';
import WeeklyBoard from './routes/board/Weekly.jsx';
import ScheduleBoard from './routes/schedule/index.jsx';
import AppShell from './components/layout/AppShell.jsx';

function RequireAuth({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <div className="p-8 text-ink-400">Loading…</div>;
  if (!user) return <Navigate to="/login" state={{ from: location }} replace />;
  return children;
}

export default function App() {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/signup" element={<Signup />} />
      <Route path="/auth/forgot" element={<ForgotPassword />} />
      <Route path="/auth/reset" element={<ResetPassword />} />

      <Route
        element={
          <RequireAuth>
            <AppShell />
          </RequireAuth>
        }
      >
        <Route index element={<Navigate to="/dashboard" replace />} />
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/board" element={<DailyBoard />} />
        <Route path="/board/week" element={<WeeklyBoard />} />
        <Route path="/schedule" element={<ScheduleBoard />} />
        <Route path="/horses" element={<HorsesList />} />
        <Route path="/horses/new" element={<HorseNew />} />
        <Route path="/horses/:id" element={<HorseDetail />} />
        <Route path="/horses/:id/edit" element={<HorseEdit />} />
        <Route path="/owners" element={<OwnersDirectory />} />
        <Route path="/owners/:id" element={<OwnerDetail />} />
        <Route path="/riders" element={<RidersDirectory />} />
        <Route path="/riders/:id" element={<RiderDetail />} />
        <Route path="/providers" element={<ServiceProviders />} />
        <Route path="/team" element={<TeamPage />} />
        <Route path="/settings/lesson-types" element={<LessonTypesPage />} />
        <Route path="/settings/shift-types" element={<ShiftTypesPage />} />
        <Route path="/settings/routines" element={<RoutinesPage />} />
        <Route path="/settings/barn" element={<BarnSettingsPage />} />
        <Route path="/settings/archive" element={<ArchivePage />} />
        <Route path="/supplements" element={<SupplementsPage />} />
        {/* Literal segments before /:id, or "new" and "history" load a product. */}
        <Route path="/supplements/new" element={<SupplementEdit />} />
        <Route path="/supplements/history" element={<SupplementHistory />} />
        <Route path="/supplements/orders" element={<SupplementOrders />} />
        <Route path="/supplements/:id" element={<SupplementDetail />} />
        <Route path="/supplements/:id/edit" element={<SupplementEdit />} />
        <Route path="/schedule/book" element={<BookPage />} />
        <Route path="/schedule/availability" element={<AvailabilityPage />} />
        <Route path="/schedule/shifts" element={<ShiftsPage />} />
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
